import { supabase } from '../lib/supabaseClient';

// Generate a persistent session ID for anonymous tracking
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Track page views only once per session per page
const hasTrackedPageView = (page: string): boolean => {
  const key = `page_view_tracked_${page}`;
  return sessionStorage.getItem(key) === 'true';
};

const markPageViewTracked = (page: string): void => {
  const key = `page_view_tracked_${page}`;
  sessionStorage.setItem(key, 'true');
};

export type AnalyticsEventType =
  | 'registration_started'
  | 'registration_completed'
  | 'registration_failed'
  | 'signup_button_clicked'
  | 'vybe_create_started'
  | 'vybe_create_completed'
  | 'vybe_create_failed'
  | 'vybe_apply_started'
  | 'vybe_apply_completed'
  | 'vybe_apply_failed'
  | 'login_attempt'
  | 'login_success'
  | 'login_failed'
  | 'verification_started'
  | 'verification_completed'
  | 'verification_failed'
  | 'subscription_attempt'
  | 'subscription_success'
  | 'subscription_failed'
  | 'topup_attempt'
  | 'topup_success'
  | 'topup_failed'
  | 'page_view'
  | 'email_sent'
  | 'feature_used';

export interface AnalyticsMetadata {
  [key: string]: any;
}

export const trackEvent = async (
  eventType: AnalyticsEventType,
  metadata: AnalyticsMetadata = {},
  userId?: string
): Promise<void> => {
  try {
    const sessionId = getSessionId();

    await supabase.from('analytics_events').insert({
      event_type: eventType,
      user_id: userId || null,
      metadata: metadata,
      session_id: sessionId,
      user_agent: navigator.userAgent,
    });
  } catch (error) {
    // Fail silently - analytics should never break the app
    console.debug('Analytics tracking failed:', error);
  }
};

// Track page view - only once per session per page
export const trackPageView = async (
  page: string,
  additionalMetadata: AnalyticsMetadata = {},
  userId?: string
): Promise<void> => {
  // Check if we've already tracked this page in this session
  if (hasTrackedPageView(page)) {
    return;
  }

  // Track the page view
  await trackEvent('page_view', { page, ...additionalMetadata }, userId);

  // Mark as tracked for this session
  markPageViewTracked(page);
};

export interface AnalyticsSummary {
  total_users: number;
  vybe_creation: {
    count: number;
  };
  vybe_application: {
    count: number;
  };
  email_verification: {
    verified_users: number;
    emails_sent: number;
  };
  veriff_verification: {
    count: number;
  };
  subscription: {
    attempts: number;
    success: number;
    failed: number;
    success_rate: number;
  };
  gender_breakdown: {
    male: { count: number; percentage: number };
    female: { count: number; percentage: number };
    non_binary: { count: number; percentage: number };
    prefer_not_to_say: { count: number; percentage: number };
  };
  age_breakdown: {
    '18_29': { count: number; percentage: number };
    '30_39': { count: number; percentage: number };
    '40_49': { count: number; percentage: number };
    '50_plus': { count: number; percentage: number };
    'unknown': { count: number; percentage: number };
  };
  email_templates: {
    welcome: number;
    acceptance: number;
    tribe_forming: number;
    joiner: number;
    cancelled: number;
    reminder: number;
  };
  page_views: {
    landing: number;
    explore: number;
    vybe_detail: number;
    profile: number;
    total: number;
  };
  waitlist_signups: number;
  abandoned_registrations: {
    total: number;
    last_24h: number;
    last_7d: number;
    last_30d: number;
  };
  total_events: number;
  date_range: {
    start: string;
    end: string;
  };
}

export const getAnalyticsSummary = async (
  startDate?: Date,
  endDate?: Date
): Promise<AnalyticsSummary | null> => {
  try {
    const { data, error } = await supabase.rpc('get_analytics_summary', {
      p_start_date: startDate?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      p_end_date: endDate?.toISOString() || new Date().toISOString(),
    });

    if (error) throw error;
    return data as AnalyticsSummary;
  } catch (error) {
    console.error('Failed to fetch analytics summary:', error);
    return null;
  }
};

export interface DailyAnalytics {
  date: string;
  event_count: number;
  unique_users: number;
  unique_sessions: number;
}

export const getDailyAnalytics = async (
  startDate?: Date,
  endDate?: Date,
  eventType?: string
): Promise<DailyAnalytics[]> => {
  try {
    const { data, error } = await supabase.rpc('get_daily_analytics', {
      p_start_date: startDate?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      p_end_date: endDate?.toISOString() || new Date().toISOString(),
      p_event_type: eventType || null,
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Failed to fetch daily analytics:', error);
    return [];
  }
};

export interface UserDetail {
  user_id: string;
  email: string;
  full_name: string;
  username: string;
  vybe_count?: number;
  application_count?: number;
  last_vybe_created?: string;
  last_application?: string;
  gender?: string;
  date_of_birth?: string;
  vybes_created?: Array<{
    vybe_id: string;
    vybe_title: string;
    created_at: string;
    date?: string;
    location?: string;
  }>;
  vybes_applied_to?: Array<{
    vybe_id: string;
    vybe_title: string;
    applied_at: string;
    vybe_date: string;
    status: string;
  }>;
  age?: number;
  created_at: string;
}

export const getVybeCreatorsDetail = async (
  startDate?: Date,
  endDate?: Date
): Promise<UserDetail[]> => {
  try {
    const { data, error } = await supabase.rpc('get_vybe_creators_detail', {
      p_start_date: startDate?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      p_end_date: endDate?.toISOString() || new Date().toISOString(),
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Failed to fetch vybe creators detail:', error);
    return [];
  }
};

export const getVybeApplicantsDetail = async (
  startDate?: Date,
  endDate?: Date
): Promise<UserDetail[]> => {
  try {
    const { data, error } = await supabase.rpc('get_vybe_applicants_detail', {
      p_start_date: startDate?.toISOString() || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      p_end_date: endDate?.toISOString() || new Date().toISOString(),
    });

    if (error) throw error;

    // Transform flat rows into grouped user data
    const userMap = new Map<string, UserDetail>();

    (data || []).forEach((row: any) => {
      const userId = row.user_id;

      if (!userMap.has(userId)) {
        userMap.set(userId, {
          user_id: userId,
          email: row.email,
          full_name: row.full_name,
          username: row.username,
          created_at: row.application_date, // Use first application date for "Joined" display
          vybes_applied_to: []
        });
      }

      const user = userMap.get(userId)!;
      user.vybes_applied_to!.push({
        vybe_id: row.vybe_id,
        vybe_title: row.vybe_title,
        applied_at: row.application_date,
        vybe_date: row.vybe_date,
        status: 'pending' // We don't have status in this query
      });
    });

    return Array.from(userMap.values());
  } catch (error) {
    console.error('Failed to fetch vybe applicants detail:', error);
    return [];
  }
};

export const getUsersByGender = async (gender: string): Promise<UserDetail[]> => {
  try {
    const { data, error } = await supabase.rpc('get_users_by_gender', {
      p_gender: gender,
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Failed to fetch users by gender:', error);
    return [];
  }
};

export const getUsersByAgeRange = async (ageRange: string): Promise<UserDetail[]> => {
  try {
    const { data, error } = await supabase.rpc('get_users_by_age_range', {
      p_age_range: ageRange,
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Failed to fetch users by age range:', error);
    return [];
  }
};

export interface AbandonedRegistration {
  id: string;
  email: string;
  step_reached: string;
  partial_data: Record<string, any> | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  referral_code: string | null;
  user_agent: string | null;
  created_at: string;
}

export const getAbandonedRegistrationsSummary = async (): Promise<{
  total: number;
  last_24h: number;
  last_7d: number;
  last_30d: number;
} | null> => {
  try {
    const { data, error } = await supabase
      .rpc('get_abandoned_registrations_summary')
      .maybeSingle();

    if (error) throw error;
    return data ? {
      total: data.total_count || 0,
      last_24h: data.last_24h_count || 0,
      last_7d: data.last_7d_count || 0,
      last_30d: data.last_30d_count || 0,
    } : null;
  } catch (error) {
    console.error('Failed to fetch abandoned registrations summary:', error);
    return null;
  }
};

export const getAbandonedRegistrationsDetails = async (
  limit: number = 100,
  offset: number = 0
): Promise<AbandonedRegistration[]> => {
  try {
    const { data, error } = await supabase.rpc('get_abandoned_registrations_details', {
      limit_param: limit,
      offset_param: offset,
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Failed to fetch abandoned registrations details:', error);
    return [];
  }
};
